import { MediaQueryService } from '@supply-mover-2-0/core';
import type {
	MediaAccessDto,
	MediaFolderDto,
	MediaTagDto,
} from '@supply-mover-2-0/shared';
import { useCallback, useEffect, useState } from 'react';

export interface MediaLibraryDataModel {
	access: MediaAccessDto | null;
	folders: Array<MediaFolderDto>;
	tags: Array<MediaTagDto>;
	/** English; the screen passes it through t(). */
	error: string | null;
	isLoading: boolean;
	reload: () => Promise<void>;
}

/**
 * PD-16339 — what every media screen needs besides the items: which brands the person
 * can file under (and whether Shared), the folders and the tags. Loaded together once;
 * `reload` after a folder or tag changes.
 */
export function useMediaLibraryData(isEnabled = true): MediaLibraryDataModel {
	const [access, setAccess] = useState<MediaAccessDto | null>(null);
	const [folders, setFolders] = useState<Array<MediaFolderDto>>([]);
	const [tags, setTags] = useState<Array<MediaTagDto>>([]);
	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	const reload = useCallback(async (): Promise<void> => {
		setIsLoading(true);
		const [accessResult, folderResult, tagResult] = await Promise.all([
			MediaQueryService.getAccess(),
			MediaQueryService.listFolders(),
			MediaQueryService.listTags(),
		]);
		setIsLoading(false);
		if (!accessResult.success) {
			setError(accessResult.error ?? 'The media library could not be loaded.');
			return;
		}
		setError(null);
		setAccess(accessResult.data);
		setFolders(folderResult.success ? folderResult.data : []);
		setTags(tagResult.success ? tagResult.data : []);
	}, []);

	useEffect(() => {
		if (isEnabled) {
			void reload();
		}
	}, [isEnabled, reload]);

	return { access, folders, tags, error, isLoading, reload };
}
